// src/hooks/useFilesComposees.ts — SCE Issues
// Les files composées de l'accueil (lot 5 du chantier du 03/11), calculées :
// leurs problèmes, leur attente la plus longue, leur résumé.
//
// Rien n'est lu qui ne serve : les vues de /issues (bloc `listeProblemes`) quand
// une file y est liée ou qu'on range l'accueil, le référentiel quand une file
// filtre sur le modèle, la liste « en fabrication » quand une file la demande.

import { useEffect, useMemo, useState } from 'react'
import { listAllEquipements } from '@/api/equipements.api'
import { charger as chargerEnFabrication } from '@/hooks/useEnFabrication'
import { useReglagesBloc } from '@/hooks/useReglagesBloc'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { CLE_BLOC_TABLEAU } from '@/hooks/useReglagesTableau'
import { cleRapprochement, type Issue } from '@/types/issue.types'
import { clesEnFabrication } from '@/utils/enFabrication'
import { identitesDeclarant } from '@/utils/identiteDeclarant'
import { lireReglages, type VuePerso } from '@/utils/tableauProblemes'
import {
  attenteDeLaFile,
  problemesDeLaFile,
  resoudreFile,
  type ContexteFile,
  type FileComposee,
} from '@/utils/filesComposees'

const MODELE_INCONNU = 'Non identifié'

/** Une file composée, prête pour les compteurs et les cartes. */
export interface FileCalculee {
  id: string
  composee: FileComposee
  libelle: string
  court: string
  issues: Issue[]
  precision: string
  attenteJours: number
  infobulle: { titre: string; lignes: string[] }
  /** Comme une file du code ; une file composée ne regroupe pas les fiches sœurs. */
  machines?: ReadonlyMap<string, number>
}

export interface UseFilesComposees {
  files: FileCalculee[]
  /** Les vues personnelles de /issues (épingler, « Depuis une vue »). */
  vues: VuePerso[]
  /** Pour l'aperçu du composeur. */
  calculer: (f: FileComposee) => Issue[]
}

export function useFilesComposees({
  composees,
  issues,
  activites,
  maintenant,
  idsSuivis,
  lireVues,
}: {
  composees: readonly FileComposee[]
  issues: readonly Issue[]
  activites: ReadonlyMap<string, number>
  maintenant: number
  idsSuivis: ReadonlySet<string>
  /** Lire les vues de /issues : une file y est liée, ou l'accueil est en cours de rangement. */
  lireVues: boolean
}): UseFilesComposees {
  const { user } = useCurrentUser()
  const actif = lireVues || composees.some((f) => f.vue)
  const { reglages } = useReglagesBloc(CLE_BLOC_TABLEAU, 'sce-issues:tableau', (brut: unknown) => lireReglages(brut, true), 'interne', actif)
  const vues = reglages.vues

  // Le référentiel (modèle) et la liste en fabrication : seulement si une file s'en sert.
  const besoinModele = composees.some((f) => f.filtres.modele?.length) || vues.some((v) => composees.some((f) => f.vue === v.id) && v.filtres.modele?.length)
  // En rangement, lue d'avance : l'aperçu du composeur la demande peut-être.
  const besoinFabrication = lireVues || composees.some((f) => f.enFabrication)
  const [modeles, setModeles] = useState<ReadonlyMap<string, string>>(new Map())
  const [enFabrication, setEnFabrication] = useState<ReadonlySet<string>>(new Set())
  useEffect(() => {
    if (!besoinModele) return
    let annule = false
    listAllEquipements()
      .then((eq) => {
        if (!annule) setModeles(new Map(eq.map((e) => [cleRapprochement(e.numeroSerie), e.familleEquipement] as const)))
      })
      .catch(() => undefined) // sans référentiel, le modèle reste « Non identifié »
    return () => {
      annule = true
    }
  }, [besoinModele])
  useEffect(() => {
    if (!besoinFabrication) return
    let annule = false
    void chargerEnFabrication().then((l) => {
      if (!annule) setEnFabrication(clesEnFabrication(l))
    })
    return () => {
      annule = true
    }
  }, [besoinFabrication])

  const ctx = useMemo<ContexteFile>(
    () => ({
      modeleDe: (i: Issue) => modeles.get(cleRapprochement(i.numeroSerie)) || MODELE_INCONNU,
      maintenant,
      mesIdentites: user ? identitesDeclarant(user) : null,
      estSuivi: (i: Issue) => idsSuivis.has(i.id),
      enFabrication: (i: Issue) => enFabrication.has(cleRapprochement(i.numeroSerie)),
      activites,
    }),
    [modeles, maintenant, user, idsSuivis, enFabrication, activites],
  )

  const files = useMemo(
    () =>
      composees.flatMap((c): FileCalculee[] => {
        const r = resoudreFile(c, vues)
        if (!r) return []
        const liste = problemesDeLaFile(r, issues, ctx)
        return [
          {
            id: c.id,
            composee: r,
            libelle: r.nom,
            court: r.nom,
            issues: liste,
            precision: r.vue ? 'Vue de Tous les problèmes' : 'File composée',
            attenteJours: attenteDeLaFile(liste, ctx),
            infobulle: {
              titre: `${liste.length} problème(s) · ${r.nom}`,
              lignes: liste.slice(0, 5).map((i) => `${i.id} · ${i.titre}`).concat(liste.length > 5 ? [`… et ${liste.length - 5} autre(s)`] : []),
            },
          },
        ]
      }),
    [composees, vues, issues, ctx],
  )

  const calculer = useMemo(() => (f: FileComposee) => {
    const r = resoudreFile(f, vues)
    return r ? problemesDeLaFile(r, issues, ctx) : []
  }, [vues, issues, ctx])

  return { files, vues, calculer }
}
